import React from 'react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { GenericFormPage } from '../../../components/generic';
import { fetcher } from '../../../services/fetcher';
import { supplyCaseRefPeekSnapshot } from '../peekRefSnapshot';
import { useSupplyCase, SupplyCase } from './useData';
import { moduleConfig, getInitialFormData, mapItemToFormData } from './config';

type RefTemplateRow = {
  entityType: string;
  allowManualReferenceEdit?: boolean;
  isActive?: boolean;
  pattern?: string;
};

const Form: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id && id !== 'new');

  const { data: templates } = useSWR<RefTemplateRow[]>('/reference-templates?moduleId=supplies', fetcher, {
    revalidateOnFocus: false,
  });
  const allowManualSupplyCaseRef =
    templates?.find((r) => r.entityType === 'SUPPLY_CASE')?.allowManualReferenceEdit !== false;

  const hasActiveSupplyCaseTemplate = Boolean(
    templates?.some(
      (r) =>
        r.entityType === 'SUPPLY_CASE' &&
        r.isActive &&
        typeof r.pattern === 'string' &&
        r.pattern.trim().length > 0,
    ),
  );

  React.useEffect(() => {
    if (!isEdit) {
      supplyCaseRefPeekSnapshot.reset();
    }
  }, [isEdit]);

  return (
    <GenericFormPage<SupplyCase>
      useData={useSupplyCase}
      config={{
        ...moduleConfig,
        getInitialFormData,
        mapItemToFormData,
        form: {
          ...moduleConfig.form,
          create: async (data: Partial<SupplyCase>) => {
            const { createSupplyCase } = await import('../../../services/mutations');
            const payload = { ...(data as Record<string, unknown>) };
            const cur = String(payload.ref ?? '').trim();
            if (hasActiveSupplyCaseTemplate && supplyCaseRefPeekSnapshot.shouldOmitRefForServerAllocation(cur)) {
              delete payload.ref;
            }
            return createSupplyCase(payload);
          },
        },
      }}
      fieldDisabled={allowManualSupplyCaseRef ? undefined : { ref: true }}
    />
  );
};

export default Form;
